import { useState } from 'react';

import Dialog, { DialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import DialogContent from '@liquidweb-dev/undercurrent/DialogContent';
import DialogTitle from '@liquidweb-dev/undercurrent/DialogTitle';
import { useSelector } from 'react-redux';

import appConfigSelectors from 'modules/appConfig/selectors';
import useIsBillingStripePaymentInFlight from 'modules/queries/billing/stripe/payment/useIsPaymentInFlight';
import useBillingBalance from 'modules/queries/billing/useBalance';
import useBillingPaymentInstrumentAllowed from 'modules/queries/billing/payment/instrument/useAllowed';
import { StartContentSkeleton } from './contents';
import { type HeldStripePaymentI } from './dialogMakePaymentContext';
import PaymentSteps from './PaymentSteps';
import titles from './titles';
import usePayablePaymentMethods from './usePayablePaymentMethods';

export interface DialogMakePaymentProps extends Omit<DialogProps, 'onClose'> {
	/**
	 * Opens the amount field with this instead of the full amount due, e.g. a past-due
	 * balance as a two-decimal string. Omit it for the usual full-amount default.
	 */
	initialAmount?: string;
	onClose: () => void;
}

const DialogMakePayment = ({
	initialAmount,
	onClose,
	open,
	...restDialogProps
}: DialogMakePaymentProps) => {
	// Every value the payment form opens with, and every option it offers, is read
	// from this data, so all of it is fetched here - above the form - and the form
	// is not mounted until it has arrived.
	const { data: billingBalanceData, isLoading: isLoadingBalance } =
		useBillingBalance({
			enabled: Boolean(open),
		});
	const { defaultPaymentMethod, paymentMethods, instrumentListQuery } =
		usePayablePaymentMethods({
			instrumentListQueryOptions: { enabled: open },
		});
	const allowedInstrumentsQuery = useBillingPaymentInstrumentAllowed({
		enabled: open,
	});

	// a Stripe payment that is being taken, or a card held and not yet captured,
	// must not be left mid-way - closing would lose the hold and invite a second
	const [heldStripePayment, setHeldStripePayment] =
		useState<HeldStripePaymentI | null>(null);
	const isPayingWithStripe = useIsBillingStripePaymentInFlight();
	const isClosable = !isPayingWithStripe && !heldStripePayment;
	const handleClose = () => {
		if (!isClosable) return;

		onClose();
	};

	// a refetch with no data reads as loading - swapping in the skeleton then
	// would remount the steps and lose a payment that is being taken or held
	const isLoading =
		isClosable &&
		(isLoadingBalance ||
			instrumentListQuery.isLoading ||
			allowedInstrumentsQuery.isLoading);

	const isPayPalAllowed = Boolean(
		allowedInstrumentsQuery.data?.allowed.some(
			(allowed) => allowed.processor === 'paypal',
		),
	);

	// Gates every Stripe path, the error fallback below included: an environment
	// with no publishable key cannot mount Elements, so offering its card entry
	// would render a payment element with no Stripe behind it.
	const stripePublishableKey = useSelector(
		appConfigSelectors.getStripePublishableKey,
	);
	const hasStripeKey = Boolean(stripePublishableKey);

	// Deliberate silent error handling here - falling back to Stripe keeps a user
	// who can't be told what is allowed from being stuck without a way to enter a
	// new card
	const isStripeAllowed =
		hasStripeKey &&
		(allowedInstrumentsQuery.isError ||
			Boolean(
				allowedInstrumentsQuery.data?.allowed.some(
					(allowed) => allowed.processor === 'stripe',
				),
			));

	return (
		<Dialog
			maxWidth="sm"
			fullWidth
			onClose={handleClose}
			open={open}
			{...restDialogProps}
		>
			{isLoading ? (
				<>
					<DialogTitle onClose={handleClose}>{titles.start}</DialogTitle>
					<DialogContent>
						<StartContentSkeleton />
					</DialogContent>
				</>
			) : (
				<PaymentSteps
					accountBalanceDisplay={
						billingBalanceData?.accountBalanceDisplay || '0'
					}
					defaultPaymentInstrumentId={(
						(!defaultPaymentMethod?.isExpired &&
							defaultPaymentMethod?.instrumentId) ||
						''
					).toString()}
					hasPaymentMethodsError={instrumentListQuery.isError}
					heldStripePayment={heldStripePayment}
					initialAmount={initialAmount}
					isClosable={isClosable}
					isPayPalAllowed={isPayPalAllowed}
					isStripeAllowed={isStripeAllowed}
					open={open}
					paymentMethods={paymentMethods}
					onClose={onClose}
					refetchPaymentMethods={() => instrumentListQuery.refetch()}
					setHeldStripePayment={setHeldStripePayment}
				/>
			)}
		</Dialog>
	);
};

export default DialogMakePayment;
